import { Dropdown, type DropdownProps } from 'primereact/dropdown';
import flagStyles from './flags.module.css';
import FlagsModel, { Flag } from '@/modules/authentication/sign-up/components/CountryFlags';
import { cn } from '@/components/ui/cn';
import { TextBodyLarge } from '@/components/text/Texts';
import { Row } from '@/components/container/Container';
import { useState } from 'react';
import { MdArrowDropDown } from 'react-icons/md';

type SelectCountryIconDropdownProps = {
	rootClassName?: (isFocused: boolean) => string;
} & DropdownProps;

export const SelectCountryIconDropdownConcierge = (props: SelectCountryIconDropdownProps) => {
	const [isShow, setIsShow] = useState(false);
	const [isFocus, setIsFocus] = useState(false);

	const dropdownIcon = (
		<MdArrowDropDown
			size={24}
			className={cn(
				'text-black',
				'transition-transform duration-300',
				isShow ? 'rotate-0' : '-rotate-180'
			)}
		/>
	);

	const flagSrc = (option: Flag) => cn(flagStyles['flag'], flagStyles[`flag-${option.code.toLowerCase()}`]);

	const countryOptionTemplate = (option: Flag) => {
		const isSelected = props.value?.code === option.code;

		return (
			<Row
				className={cn(
					'h-14 px-3 gap-3 items-center transition-all duration-200 cursor-pointer',
					isSelected ? 'bg-[#DCE0E3FF]' : 'hover:bg-[#DCE0E3FF]'
				)}
			>
				<img alt={option.name} src="/images/flag-placeholder.png" className={flagSrc(option)} />
				<TextBodyLarge text={option.name} className="w-full text-onSurface whitespace-nowrap truncate" />
			</Row>
		);
	};

	const selectedCountryTemplate = (option: Flag, props: DropdownProps) => {
		if (!option) return <span>{props.placeholder}</span>;
		return (<img alt={option.name} src="/images/flag-placeholder.png" className={flagSrc(option)} />);
	};

	const calcRootClass = () => props.rootClassName?.(isShow || isFocus);

	return (
		<Dropdown
			unstyled
			dropdownIcon={dropdownIcon} collapseIcon={dropdownIcon}
			onShow={() => setIsShow(true)} onHide={() => setIsShow(false)}
			onFocus={() => setIsFocus(true)} onBlur={() => setIsFocus(false)}
			virtualScrollerOptions={{ itemSize: 56 }}

			options={FlagsModel.countries}
			optionLabel="name"
			valueTemplate={selectedCountryTemplate}
			itemTemplate={countryOptionTemplate}
			value={props.value}
			onChange={props.onChange}

			pt={{
				root: { className: cn(
					'flex items-center justify-between p-4',
					'w-[104px] h-14 shrink-0 bg-transparent rounded-[4px]',
					'transition-all duration-200', isShow || isFocus
						? 'shadow-[inset_0_0_0_3px_theme(colors.primary60)]'
						: 'shadow-[inset_0_0_0_1px_theme(colors.primary60)]',
					calcRootClass()
				)},
				panel: { className: cn(
					'w-[216px] h-[240px] bg-surfaceContainer rounded-b-[4px]',
					'blue-scroll pr-1 py-2',
					'shadow-[0px_2px_6px_2px_#00000026,0px_1px_2px_0px_#0000004D]',
				)},
				virtualScroller: { root: { className: '!h-full pr-1' } },
				list: { className: 'w-[204px] !min-w-0' },
				item: { className: 'outline-0' },
			}}
		/>
	);
};

// Normally we extend main version by concierge version. But here we had a different story...
export const SelectCountryIconDropdown = (props: SelectCountryIconDropdownProps) => (
	<SelectCountryIconDropdownConcierge
		{...props}
		rootClassName={(isFocused) => cn(
			'bg-surfaceContainerHighest rounded-b-none', isFocused
				? '!shadow-[inset_0_-3px_0_0_theme(colors.primary)]'
				: '!shadow-[inset_0_-1px_0_0_theme(colors.onSurfaceVariant)]'
		)}
	/>
);
